import { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

/**
 * RichTextField — a formatting-rich editor for the free-text fields on the
 * resource-person course builder: course description, introduction, learning
 * outcomes, requirements and audience, plus each module description and each
 * lesson's content.
 *
 * Why react-quill: it is already a dependency of this app (react-quill@2, the
 * React 18 compatible fork) and is used the same way in
 * `src/staffPages/NewsManagement.jsx`, so this introduces no new library.
 *
 * Two behaviours the author asked for:
 *  1. "accepts all formatting" → a full inline/block toolbar (see FORMATS).
 *  2. Long content must stay usable: the editor grows with what you type/paste
 *     up to `maxHeight`, then scrolls internally. That way a short field has no
 *     dead space, and a long paste never pushes the rest of the form off
 *     screen. Both limits are passed to CSS as custom properties, so the
 *     scrollbar appears exactly when the content outgrows the box.
 */

/** Every text format Quill can round-trip. Images/video are intentionally
 *  omitted: they need an upload endpoint, and inline media is not part of a
 *  course description. */
const FORMATS = [
  "bold", "italic", "underline", "strike",
  "blockquote", "code-block", "code",
  "header", "list", "bullet", "indent",
  "link", "color", "background", "align",
  "size", "script", "direction",
];

const MODULES = {
  toolbar: [
    [{header: [1, 2, 3, false]}],
    ["bold", "italic", "underline", "strike"],
    [{list: "ordered"}, {list: "bullet"}, {indent: "-1"}, {indent: "+1"}],
    [{align: []}],
    ["blockquote", "code-block"],
    [{color: []}, {background: []}],
    ["link"],
    ["clean"],
  ],
  clipboard: {matchVisual: false},
};

/**
 * Quill's canonical "empty document" markup.
 *
 * The value we hand to <ReactQuill> must match what Quill itself renders, or
 * react-quill keeps resetting the editor contents to reconcile the difference —
 * and each reset emits a text-change, which calls our onChange → setState →
 * re-render. That is the "Maximum update depth exceeded" loop.
 *
 * So: pass `EMPTY_HTML` for a blank field (exactly what Quill renders) and pass
 * Quill's output back to the parent verbatim. Clean-up of the placeholder markup
 * happens once, at save time, via `cleanHtmlForSave`.
 */
const EMPTY_HTML = "<p><br></p>";

/** Normalise the incoming value to the shape Quill expects. */
function toEditorValue(value) {
  return typeof value === "string" && value.trim() !== "" ? value : EMPTY_HTML;
}

/**
 * Strip Quill's placeholder markup for persistence. Only collapses to "" when
 * the whole document is empty paragraphs — real content is returned untouched.
 * Call this in the save/submit payload, never in the render path.
 */
export function cleanHtmlForSave(value) {
  if (typeof value !== "string") return "";
  const withoutEmptyParagraphs = value.replace(/<p>(<br\s*\/?>|\s|&nbsp;)*<\/p>/gi, "");
  return withoutEmptyParagraphs.trim() === "" ? "" : value;
}

export function RichTextField({
  value = "",
  onChange,
  placeholder = "",
  disabled = false,
  id,
  minHeight = 140,
  maxHeight = 220,
  className = "",
}) {
  // Keep the editor's own value and only accept an external value when it did
  // not come from this editor. Passing the parent value straight back into
  // ReactQuill lets tiny normalisation differences echo forever:
  // onChange → parent setState → new value prop → Quill resets → onChange …
  const [content, setContent] = useState(() => toEditorValue(value));
  const lastEmitted = useRef(toEditorValue(value));

  useEffect(() => {
    if (value === undefined) return;
    if (value === lastEmitted.current) return;
    const next = toEditorValue(value);
    setContent((current) => (current === next ? current : next));
  }, [value]);

  const handleChange = (next) => {
    lastEmitted.current = next;
    setContent(next);
    onChange?.(next);
  };

  return (
    <div
      className={`rte-shell ${className}`}
      id={id}
      data-disabled={disabled ? "true" : undefined}
      style={{"--rte-min": `${minHeight}px`, "--rte-max": `${maxHeight}px`}}
    >
      <ReactQuill
        theme="snow"
        value={content}
        onChange={handleChange}
        modules={MODULES}
        formats={FORMATS}
        placeholder={placeholder}
        readOnly={disabled}
      />
    </div>
  );
}

export default RichTextField;